import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { useEditor } from "@/components/editor/useEditorState";

export const BUSY = ["rendering", "analyzing", "fixing"];
const DONE_MSG = {
  rendered: "Screenshot pronti: controlla la stima e avvia l'analisi",
  analyzed: "Analisi visiva pronta: scegli cosa correggere",
  ready: "Correzioni pronte: confronta prima e dopo e approva",
};

// Latest visual review of the project (list always, detail + polling while the sheet is open or a step runs).
export function useVisualReview(open) {
  const ed = useEditor();
  const { api, id } = ed;
  const [list, setList] = useState(null);
  const [rev, setRev] = useState(null);
  const [busy, setBusy] = useState("");
  const prev = useRef(null);
  const genStatus = ed.project?.generation?.status;

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/visual-reviews`);
    setList(data);
    const cur = data.reviews[0]?.status === "discarded" ? null : data.reviews[0];
    if (cur && (open || BUSY.includes(cur.status))) setRev((await api.get(`/projects/${id}/visual-reviews/${cur.id}`)).data);
    else setRev(cur || null);
    return cur;
  }, [api, id, open]);

  useEffect(() => {
    refresh().catch(() => setList(false));
    if (genStatus !== "done") return undefined;
    const t = setTimeout(() => refresh().catch(() => {}), 4000);
    return () => clearTimeout(t);
  }, [refresh, genStatus]);
  useEffect(() => { ed.setUi({ reviewStatus: rev?.status || null }); }, [rev?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!rev || !BUSY.includes(rev.status)) return undefined;
    const t = setInterval(() => refresh().catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [rev?.id, rev?.status, refresh]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const was = prev.current;
    prev.current = rev ? `${rev.id}:${rev.status}` : null;
    if (!rev || !was || !was.startsWith(rev.id) || !BUSY.includes(was.split(":")[1]) || BUSY.includes(rev.status)) return;
    if (rev.error) toast.message("Revisione visiva", { description: rev.error });
    else if (DONE_MSG[rev.status]) toast.success(DONE_MSG[rev.status]);
  }, [rev?.id, rev?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(ok); return r; } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(""); }
  };
  const post = (path, body = {}) => api.post(`/projects/${id}/visual-reviews/${rev.id}${path}`, body).then((r) => r.data);
  const start = (scope) => run("start", async () => { await ed.save(); await api.post(`/projects/${id}/visual-reviews`, { scope }); await refresh(); });
  const analyze = (est) => run("analyze", async () => { await post("/analyze", { confirm_usd: est?.usd }); await refresh(); });
  const fix = (body, est) => run("fix", async () => { await ed.save(); await post("/fix", { ...body, confirm_usd: est?.usd }); await refresh(); });
  const discard = () => run("discard", async () => { await post("/discard"); await refresh(); }, "Revisione archiviata");
  const dismiss = (did, dismissed) => run(`dismiss-${did}`, async () => { await post(`/defects/${did}/dismiss`, { dismissed }); await refresh(); });
  const apply = (exclude) => run("apply", async () => {
    await ed.save();
    const data = await post("/apply", { exclude });
    ed.replaceContent(data.content, { keepHistory: true });
    ed.setProject((p) => ({ ...p, updated_at: new Date().toISOString() }));
    await refresh();
  }, "Correzioni applicate · versione precedente salvata");
  const undo = async (force = false) => {
    setBusy("undo");
    try {
      await ed.save();
      const data = await post("/undo", { force });
      ed.replaceContent(data.content, { keepHistory: true });
      await refresh();
      toast.success("Correzioni annullate");
      return "ok";
    } catch (e) {
      const msg = errMsg(e);
      if (e?.response?.status === 409 && msg.startsWith("MODIFIED:")) return msg.replace("MODIFIED: ", "");
      toast.error(msg);
      return null;
    } finally { setBusy(""); }
  };
  return { list, rev, busy, refresh, start, analyze, fix, discard, dismiss, apply, undo };
}
